import { useEffect, useState, useCallback } from "react";

/**
 * usePWAInstall — captures the `beforeinstallprompt` event and exposes
 * an `install()` function + `available` boolean.
 * Also detects if the app is already installed (standalone).
 */
export default function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState("unknown"); // 'ios' | 'android' | 'desktop' | 'unknown'

  useEffect(() => {
    // Detect standalone / already-installed
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(isStandalone);

    // Detect platform for iOS instructions (iOS Safari has no beforeinstallprompt)
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/.test(ua)) setPlatform("ios");
    else if (/Android/.test(ua)) setPlatform("android");
    else setPlatform("desktop");

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return { ok: false, reason: "not-available" };
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return { ok: outcome === "accepted", outcome };
  }, [deferredPrompt]);

  // "available" == the browser fired beforeinstallprompt OR we're on iOS (manual instructions)
  const available = !!deferredPrompt || (platform === "ios" && !installed);

  return { available, installed, platform, install, hasNativePrompt: !!deferredPrompt };
}
